/**
 * Projektfotos — Galerie und Hochladen an einer Stelle, getrennt nach Phase.
 *
 * Die Bilder saßen bis zum 15.08.2026 im Projektgedächtnis, zwischen Briefing
 * und Verlauf. Dort sind sie schwer zu finden: wer nach dem Papier eines
 * Projekts sucht, schaut unter „Dokumente". Fotos sind Dokumente desselben
 * Projekts und gehören dorthin.
 *
 * Seit dem 16.08. trägt jedes Bild eine **Phase**. Ohne sie beantwortet ein
 * Projektfoto nichts: „Hecke stark schneiden" vor den Arbeiten ist ein Auftrag,
 * dasselbe Bild danach wäre eine Reklamation. Beim Hochladen gilt **ein**
 * Umschalter für den ganzen Schwung — wer zwölf Aufnahmen vom selben Termin
 * hochlädt, tippt einmal statt zwölfmal. Danach lässt sich jedes Bild einzeln
 * umhängen, denn in einem Schwung ist manchmal eins, das woanders hingehört.
 *
 * Die Daten kommen aus `useProjektAkte` — derselben Abfrage, die auch das
 * Projektgedächtnis speist. Ein eigener Endpunkt für dieselben Zeilen wäre ein
 * zweiter Weg auf dieselben Daten; der Cache trägt beide Ansichten.
 */
import { useMemo, useRef, useState } from "react";
import {
  useCreateProjektAufnahme,
  useProjektAkte,
  useUpdateProjektAufnahme,
} from "../../hooks/usePlanung";
import { AUFNAHME_PHASEN, type AufnahmePhase, type ProjektAufnahme } from "../../lib/types";
import { errorText as fehlertext } from "../../lib/invoices";

type Bild = { titel: string; unterzeile: string; url: string };

const BILDTYPEN = "image/jpeg,image/png,image/webp,image/heic,image/heif";
/** Deckungsgleich mit `kontor.MAX_AUFNAHMEN_PRO_UPLOAD` und der Team-App. */
const MAX_PRO_UPLOAD = 20;
const PHASE_LABEL: Record<AufnahmePhase, string> = {
  bestandsaufnahme: "Vorher · Bestandsaufnahme",
  verlauf: "Verlauf · Arbeitstage",
  ergebnis: "Nachher · Ergebnis",
  sonstiges: "Sonstiges",
};

/** „2026-08-11" → „Dienstag, 11.08.2026" — ein Tag liest sich als Tag. */
function tagLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Verlauf-Bilder nach Arbeitstag bündeln, neueste zuerst; ohne Tag ans Ende. */
function tageVon(bilder: ProjektAufnahme[]): [string, ProjektAufnahme[]][] {
  const nach = new Map<string, ProjektAufnahme[]>();
  for (const b of bilder) {
    const tag = b.arbeitstag || "";
    const liste = nach.get(tag);
    if (liste) liste.push(b); else nach.set(tag, [b]);
  }
  return [...nach.entries()].sort((a, z) => (z[0] || "").localeCompare(a[0] || ""));
}

function zeit(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

export default function Projektdokumentation({
  projektId,
  onBild,
}: {
  projektId: string;
  onBild: (bild: Bild) => void;
}) {
  const { data, isLoading, isError } = useProjektAkte(projektId);
  const upload = useCreateProjektAufnahme(projektId);
  const aendern = useUpdateProjektAufnahme(projektId);
  const [offen, setOffen] = useState(false);
  const [dateien, setDateien] = useState<File[]>([]);
  const [notiz, setNotiz] = useState("");
  const [phase, setPhase] = useState<AufnahmePhase>("bestandsaufnahme");
  // Nur der Verlauf kennt Tage. Vorgabe ist heute — der Normalfall ist „ich war
  // heute auf der Baustelle"; wer Bilder nachträgt, stellt das Datum zurück.
  const [arbeitstag, setArbeitstag] = useState(() => new Date().toISOString().slice(0, 10));
  const [abgelehnt, setAbgelehnt] = useState<string[]>([]);
  const [ohneOrt, setOhneOrt] = useState(0);
  const dateiFeld = useRef<HTMLInputElement>(null);

  const aufnahmen = useMemo(() => data?.aufnahmen ?? [], [data]);
  /* Nach Phase gruppiert statt chronologisch: die Frage an eine Fotoliste ist
     fast immer „wie sah es vorher aus" oder „was ist daraus geworden", nicht
     „was wurde als drittes fotografiert". */
  const gruppen = useMemo(
    () => AUFNAHME_PHASEN
      .map(({ id, label, hinweis }) => ({
        id, label, hinweis,
        bilder: aufnahmen.filter((a: ProjektAufnahme) => a.phase === id),
      }))
      .filter((g) => g.bilder.length > 0),
    [aufnahmen],
  );

  function speichern() {
    if (!dateien.length) return;
    setAbgelehnt([]);
    setOhneOrt(0);
    upload.mutate(
      {
        dateien,
        notiz: notiz.trim(),
        phase,
        arbeitstag: phase === "verlauf" ? arbeitstag : undefined,
      },
      {
        onSuccess: (ergebnis) => {
          // Teilerfolg bleibt stehen: die abgelehnten Bilder werden benannt,
          // das Formular offen gehalten. Alles zuzuklappen hieße, dem Nutzer
          // die Information wegzunehmen, welche drei von zwanzig fehlen.
          setAbgelehnt(ergebnis.abgelehnt);
          setOhneOrt(ergebnis.ohne_ort ?? 0);
          setDateien([]);
          if (dateiFeld.current) dateiFeld.current.value = "";
          if (!ergebnis.abgelehnt.length) {
            setNotiz("");
            setOffen(false);
          }
        },
      },
    );
  }

  return (
    <div className="vg-unterblock">
      <div className="vg-doku-kopf">
        <span className="vg-detail-section-title">Fotos</span>
        <button type="button" className="vg-btn" onClick={() => setOffen((z) => !z)}>
          {offen ? "Abbrechen" : "+ Foto"}
        </button>
      </div>

      {offen && (
        <div className="vg-akte-form vg-gedaechtnis-form">
          <div className="is-breit">
            <span className="vg-doku-feldlabel">Wann ist das Bild entstanden?</span>
            <div className="vg-proj-tabs" role="group" aria-label="Phase der Aufnahme">
              {AUFNAHME_PHASEN.map(({ id, label, hinweis }) => (
                <button
                  key={id}
                  type="button"
                  title={hinweis}
                  aria-pressed={phase === id}
                  className={`vg-proj-tab${phase === id ? " is-active" : ""}`}
                  onClick={() => setPhase(id)}
                >
                  {label}
                </button>
              ))}
            </div>
            <small className="vg-doku-hinweis">
              {AUFNAHME_PHASEN.find((p) => p.id === phase)?.hinweis}
            </small>
          </div>
          {/* Nur der Verlauf wächst über Wochen und braucht deshalb einen Tag.
              Vorher und Nachher sind je ein Anlass — dort wäre die Frage
              überflüssig. */}
          {phase === "verlauf" && (
            <label>
              Arbeitstag
              <input
                type="date"
                value={arbeitstag}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setArbeitstag(e.target.value || arbeitstag)}
              />
            </label>
          )}
          <label className="is-breit">
            Projektfotos <span className="vg-doku-hinweis">— mehrere auf einmal möglich</span>
            <input
              ref={dateiFeld}
              type="file"
              multiple
              accept={BILDTYPEN}
              onChange={(e) => setDateien(Array.from(e.target.files ?? []))}
            />
          </label>
          {dateien.length > 0 && (
            <small className="vg-doku-hinweis is-breit">
              {dateien.length === 1 ? "1 Bild ausgewählt" : `${dateien.length} Bilder ausgewählt`}
              {dateien.length > MAX_PRO_UPLOAD && ` — höchstens ${MAX_PRO_UPLOAD} auf einmal`}
            </small>
          )}
          <label className="is-breit">
            Was ist auf den Bildern wichtig?
            <textarea
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              rows={2}
              placeholder="Zum Beispiel: Eibe auf etwa 2,5 m einkürzen"
            />
          </label>
          {abgelehnt.length > 0 && (
            <div className="vg-banner is-warn is-breit">
              {abgelehnt.length === 1 ? "Ein Bild wurde nicht gespeichert:" : `${abgelehnt.length} Bilder wurden nicht gespeichert:`}
              <ul>{abgelehnt.map((grund) => <li key={grund}>{grund}</li>)}</ul>
            </div>
          )}
          <div className="vg-akte-form-actions">
            {upload.isError && <span>{fehlertext(upload.error)}</span>}
            <button
              type="button"
              className="vg-btn vg-btn-primary"
              disabled={!dateien.length || dateien.length > MAX_PRO_UPLOAD || upload.isPending}
              onClick={speichern}
            >
              {upload.isPending
                ? `Lädt ${dateien.length === 1 ? "hoch" : `${dateien.length} Bilder hoch`} …`
                : dateien.length > 1
                  ? `${dateien.length} Fotos im Projekt speichern`
                  : "Foto im Projekt speichern"}
            </button>
          </div>
        </div>
      )}
      {/* Am Projekt ist der Ort keine Pflicht (das Projekt ist ja bekannt) — aber wer
          ihn verliert, soll es erfahren: ohne Ort kann Kontor das Bild später nirgends
          sonst einordnen, und am iPhone fehlt oft nur ein Schalter. */}
      {ohneOrt > 0 && (
        <div className="vg-banner is-warn">
          {ohneOrt === 1 ? "Ein Foto kam ohne Aufnahmeort an." : `${ohneOrt} Fotos kamen ohne Aufnahmeort an.`}{" "}
          Gespeichert ist es trotzdem. Am iPhone im Auswahlfenster „Optionen“ → „Ort“ einschalten.
        </div>
      )}

      {isLoading && <div className="vg-detail-section-hint">Fotos werden geladen …</div>}
      {isError && (
        <div className="vg-detail-section-hint">
          Die Fotos ließen sich nicht laden. Ein erneutes Öffnen der Seite hilft meistens.
        </div>
      )}

      {!isLoading && !isError && (
        gruppen.length ? (
          gruppen.map((gruppe) => (
            <div className="vg-doku-gruppe" key={gruppe.id}>
              <div className="vg-doku-gruppe-titel">
                {PHASE_LABEL[gruppe.id]} <span className="vg-proj-tab-count">{gruppe.bilder.length}</span>
              </div>
              {/* Nur der Verlauf wird nach Tagen untergliedert: er waechst ueber
                  Wochen. Vorher und Nachher sind je ein Anlass — dort waere eine
                  Tagesueberschrift eine Zeile ohne Inhalt. */}
              {gruppe.id === "verlauf" ? (
                tageVon(gruppe.bilder).map(([tag, bilder]) => (
                  <div className="vg-doku-tag" key={tag || "ohne-tag"}>
                    <div className="vg-doku-tag-titel">
                      {tag ? tagLabel(tag) : "Ohne Tageszuordnung"}
                      <span className="vg-proj-tab-count">{bilder.length}</span>
                    </div>
                    <div className="vg-akte-fotos">
                      {bilder.map((a) => (
                        <FotoKarte key={a.id} aufnahme={a} onBild={onBild} aendern={aendern} />
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="vg-akte-fotos">
                  {gruppe.bilder.map((a: ProjektAufnahme) => (
                    <FotoKarte key={a.id} aufnahme={a} onBild={onBild} aendern={aendern} />
                  ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="vg-detail-section-hint">
            Noch kein Foto zu diesem Projekt.
          </div>
        )
      )}
    </div>
  );
}


/* Eine Foto-Karte. Eigene Komponente, seit der Verlauf zusaetzlich nach Tagen
   gruppiert wird: sonst staende dasselbe Markup zweimal da und waere beim
   naechsten Fix einmal vergessen worden. */
function FotoKarte({
  aufnahme,
  onBild,
  aendern,
}: {
  aufnahme: ProjektAufnahme;
  onBild: (b: Bild) => void;
  aendern: { isPending: boolean; mutate: (v: { id: string; notiz: string; phase: AufnahmePhase }) => void };
}) {
  return (
  <div className="vg-akte-foto-karte" key={aufnahme.id}>
    <button
      type="button"
      className="vg-akte-foto"
      onClick={() => onBild({
        titel: aufnahme.notiz || "Projektfoto",
        unterzeile: `${zeit(aufnahme.aufgenommen_am || aufnahme.created_at)} · ${PHASE_LABEL[aufnahme.phase]}`,
        url: aufnahme.bild_url,
      })}
    >
      <img
        src={aufnahme.vorschau_url}
        alt={aufnahme.notiz || "Projektfoto ohne Beschreibung"}
        loading="lazy"
      />
      <span>
        <strong>{aufnahme.notiz || "Beschreibung fehlt"}</strong>
        <small>
          {zeit(aufnahme.aufgenommen_am || aufnahme.created_at)} · {aufnahme.quelle_typ === "kontor" ? "Kontor" : aufnahme.quelle_typ === "eingang" ? "einsortiert" : aufnahme.quelle_typ === "mail" ? "aus Mail" : "Aufnahme"}
        </small>
      </span>
    </button>
    {/* Einzelnes Umhängen: der Umschalter oben gilt für den
        ganzen Schwung, und darin ist manchmal ein Bild, das
        woanders hingehört. */}
    <label className="vg-doku-umhaengen">
      <span className="vg-sr-only">Phase von {aufnahme.notiz || "diesem Bild"} ändern</span>
      <select
        value={aufnahme.phase}
        disabled={aendern.isPending}
        onChange={(e) => aendern.mutate({
          id: aufnahme.id,
          notiz: aufnahme.notiz,
          phase: e.target.value as AufnahmePhase,
        })}
      >
        {AUFNAHME_PHASEN.map(({ id, label }) => (
          <option key={id} value={id}>{label}</option>
        ))}
      </select>
    </label>
  </div>
  );
}
